import { reactive } from 'vue';
import { isMobile, isRetina, isApp } from '@/core/utils/device';
import Page from '@/core/core/page';
import Events from '@/core/core/events';
import options from '@/core/core/options';
import state from '@/core/core/state';
import directiveTooltip from '@/core/directives/tooltip';

const _window: any = window;

class Core {

	/**
	 * Настройки словаря
	 */
	static L = reactive({
		Not_date: 'Not date',
	});

	static page = _window?.page || {};

	static Page = Page;

	static options = options;

	static state = reactive({ ...state });

	static matchMediaIsMobile?: MediaQueryList;

	/**
	 * Добавить на страницу стили, используется для загрузки стилей из js
	 *
	 * Условная загрузка стилей m и pc отключена в пользу производительсности, стили грузятся всегда, но применяются по условию
	 * @param style - css стили в строке
	 * @param type - если указать 'm' или 'pc', то стили будут применяться по условию в зависимости от настройки this.options.widthForMobile
	 */
	static appendStyle(style: string, type: '' | 'm' | 'pc' = ''): void {
		let media = 'all';

		if (type === 'm') {
			media = '(max-width: ' + this.options.widthForMobile + 'px)';
		}

		if (type === 'pc') {
			media = '(min-width: ' + this.options.widthForMobile + 'px)';
		}

		const elStyle = document.createElement('style');
		elStyle.innerHTML = style;
		elStyle.media = media;

		document.head.append(elStyle);
	}

	/**
	 * Установить как плагин в прилоежнии Vue
	 * Core.state является общим для всех приложений Vue на странице
	 * Core.options является общим для всех приложений Vue на странице
	 * @param app - Vue App
	 * @param {typeof options} options - параметры UI
	 */
	static install(app: any, options: typeof this.options) {
		Core.defineOptions(options);

		app.provide('top-core', Core);

		this.installDirectives(app);
	}

	static installDirectives(app: any) {
		app.directive('top-tooltip', directiveTooltip);
	}

	/**
	 * Установить конфигурацию UI
	 * @param {typeof options} options
	 */
	static defineOptions(options: typeof this.options) {
		if (options?.widthForMobile) {
			this.options.widthForMobile = options.widthForMobile;
		}

		if (options?.dateFormat) {
			this.options.dateFormat = options.dateFormat;
		}

		if (options?.gmt) {
			this.options.gmt = options.gmt;
		}

		Core.setState();
	}

	static setState() {
		Core.matchMediaIsMobile = window.matchMedia(`(max-width: ${Core.options.widthForMobile}px)`);

		Core.state.isMobileUA = isMobile();
		Core.state.isRetina = isRetina();
		Core.state.isApp = isApp();
		Core.state.dateFormat = this.options.dateFormat;
		Core.state.gmt = this.options.gmt;

		Core.setStateByWindowSize();

		// пересчет значений, зависящих от рамзеров окна
		Events.addOnReize(Core.onResize);

		Core.onResize();

		Core.saveToCookie();
	}

	static onResize() {
		Core.setStateByWindowSize();

		// честный vh
		document.documentElement.style.setProperty('--100vh', window.innerHeight + 'px');
	}

	static setStateByWindowSize() {
		Core.state.isMobile = !!Core.matchMediaIsMobile?.matches;
		Core.state.isLandscape = (window.innerWidth > window.innerHeight);
		Core.state.isPortrait = !Core.state.isLandscape;
		Core.state.size = (window.innerWidth > window.innerHeight) ? window.innerWidth : window.innerHeight;

		Core.saveToCookie();
	}

	/**
	 * Сохранить информацию об устройстве в cookie
	 */
	static saveToCookie() {
		const device = [
			1,
			window.innerWidth,
			window.innerHeight,
			window.devicePixelRatio,
			Number(Core.state.isMobile),
			Number(Core.state.isRetina),
		];

		document.cookie = 'device=' + device.join(',') + '; path=/;';
	}

}

export default Core;